import React, {forwardRef, useEffect, useRef, useState} from "react";
import {Controller, useFormState} from "react-hook-form";
import moment from "moment";
import {InputAdornment, TextField} from "@mui/material";
import {getNestedError} from "utils";
import {Svg} from "assets";

export const InputMain = forwardRef(
    (
        {
            className,
            label,
            labelHeader,
            placeholder,
            rules,
            control,
            name,
            type = "text",
            maxLength,
            startAdornment,
            endAdornment,
            onClickIconStart,
            onClickIconEnd,
            disabled,
            min,
            max,
            onFocus,
            onBlur,
            allowErrorMessage = true,
            ...anotherProps
        },
        ref,
    ) => {
        const [showPassword, setShowPassword] = useState(false);
        const inputRef = useRef(null);
        const prevHasError = useRef(false);
        const { errors } = useFormState({ control });
        const error = getNestedError(errors, name);
        const hasError = !!error?.type;

        const typePassword = showPassword ? "text" : "password";
        const EndIcon = showPassword ? Svg.EyeSvg : Svg.EyeSlashSvg;

        useEffect(() => {
            if (hasError && !prevHasError.current && inputRef.current) {
                inputRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
            }
            prevHasError.current = hasError;
        }, [hasError]);

        const onLength = (e) => {
            if (maxLength) {
                return (e.target.value = e.target.value.slice(0, maxLength));
            }
        };

        return (
            <div
                className={`input-main-wrapper input-custom-wrapper ${
                    className ? className : ""
                }`}
                ref={ref}
            >
                <Controller
                    control={control}
                    name={name}
                    rules={rules}
                    render={({field, formState: {errors}}) => {
                        return (
                            <>
                                {labelHeader && (
                                    <p
                                        className={`input-main-label-header ${
                                            error?.message?.message ? "errorLabel" : ""
                                        }`}
                                    >
                                        {labelHeader}
                                        {rules?.required && (
                                            <span className="input-main-label-header-required">
                        *
                      </span>
                                        )}
                                    </p>
                                )}
                                <TextField
                                    onInput={(e) => onLength(e)}
                                    inputRef={(el) => {
                                        field.ref(el);
                                        inputRef.current = el;
                                    }}
                                    disabled={disabled}
                                    {...anotherProps}
                                    {...field}
                                    onWheel={(e) => {
                                        if (type === "number") {
                                            e?.preventDefault();
                                            e.target.blur();
                                        }
                                    }}
                                    onChange={(event) => {
                                        if (type === "number" && +event.target.value < 0) {
                                            event.target.value = 0;
                                        }
                                        field?.onChange(event);
                                    }}
                                    autoComplete="off"
                                    error={hasError}
                                    type={showPassword ? typePassword : type}
                                    value={field.value}
                                    placeholder={placeholder}
                                    onFocus={onFocus}
                                    onBlur={onBlur}
                                    label={label}
                                    inputProps={{
                                        min: min ? min : "",
                                        max: max ? max : "",
                                        maxLength: maxLength ? maxLength : "",
                                    }}
                                    InputProps={{
                                        startAdornment: startAdornment && (
                                            <InputAdornment
                                                onClick={onClickIconStart}
                                                className={`${
                                                    error?.message?.message ? "error-svg" : ""
                                                }`}
                                                position="start"
                                            >
                                                {startAdornment}
                                            </InputAdornment>
                                        ),
                                        endAdornment:
                                            (endAdornment && (
                                                <InputAdornment
                                                    onClick={onClickIconEnd}
                                                    position="end"
                                                    className={`${
                                                        error?.message?.message ? "error-svg" : ""
                                                    }`}
                                                >
                                                    {endAdornment}
                                                </InputAdornment>
                                            )) ||
                                            (type === "password" && (
                                                <InputAdornment
                                                    onClick={(e) => {
                                                        onClickIconEnd && onClickIconEnd(e);
                                                        setShowPassword((prev) => !prev);
                                                    }}
                                                    position="end"
                                                    className={`${
                                                        error?.message?.message ? "error-svg-password" : ""
                                                    }`}
                                                >
                                                    <EndIcon/>
                                                </InputAdornment>
                                            )),
                                    }}
                                />
                                {allowErrorMessage && <p className="error-message">{error?.message}</p>}
                            </>
                        );
                    }}
                />
            </div>
        );
    },
);

InputMain.displayName = "InputMain";
